import { useCreateBrandVisualKit } from 'hooks/queries/workspaces/useCreateBrandVisualKit'
import { useBrandWizardVisualAnalysisContext } from './BrandWizardVisualAnalysisContext'
import { Button } from 'blaze-ui/Button'
import { BrandMediaType, BrandVisualKit, FontRole } from 'types'
import { getFontWeightAsNumber } from './utils'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useCreateBrandMediaFile } from 'hooks/queries/workspaces/brandVisualKits/useCreateBrandMediaFile'
import { useCreateMediaFile } from 'hooks/queries/mediaFiles/useCreateMediaFile'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { ButtonProps } from 'blaze-ui/Button/Types'

interface Props extends Pick<ButtonProps, 'fullWidth' | 'isDisabled'> {
  name: string
  onConfirm: (kit: BrandVisualKit) => void
}

export const ConfirmButton = ({ name, fullWidth, isDisabled, onConfirm }: Props) => {
  const { result } = useBrandWizardVisualAnalysisContext()
  const fontPairing = result.font_pairings[0]
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProject = useUploadsProject(workspaceId).data?.project
  const createBrandVisualKit = useCreateBrandVisualKit()
  const createBrandMediaFile = useCreateBrandMediaFile()
  const createMediaFile = useCreateMediaFile()
  const { uploadImageFiles, isUploading } = useImportMediaFiles()

  const isLoading =
    createBrandVisualKit.isLoading || createBrandMediaFile.isLoading || createMediaFile.isLoading || isUploading

  const handlePress = async () => {
    if (!uploadsProject || !fontPairing) {
      return
    }

    const { display_font: displayFont, body_font: bodyFont } = fontPairing
    const { logo, logoFile } = result

    try {
      const { brand_visual_kit: kit } = await createBrandVisualKit.mutateAsync({
        name,
        colors: result.colors.map((hex) => ({ hex })),
        text_styles: [
          {
            font_role: FontRole.Title,
            style: {
              font_family: displayFont.family,
              font_weight: getFontWeightAsNumber(displayFont.weight),
              font_size: 64,
              italic: displayFont.style === 'Italic',
            },
          },
          {
            font_role: FontRole.Body,
            style: {
              font_family: bodyFont.family,
              font_weight: getFontWeightAsNumber(bodyFont.weight),
              font_size: 16,
              italic: bodyFont.style === 'Italic',
            },
          },
        ],
      })

      if (logo) {
        let mediaFileId: number | undefined
        if (logoFile) {
          const mediaFiles = await uploadImageFiles([logoFile], uploadsProject.id)
          if (!mediaFiles) {
            throw new Error('Failed to upload logo')
          }
          mediaFileId = mediaFiles[0].media_file_id
        } else {
          const data = await createMediaFile.mutateAsync({
            source_url: logo,
            mime_type: 'image/jpeg',
            title: 'Logo',
            project_key: uploadsProject.id,
          })
          mediaFileId = data.media_file_id
        }
        if (mediaFileId) {
          await createBrandMediaFile.mutateAsync({
            brandMediaType: BrandMediaType.LOGO,
            brandVisualKitId: kit.id,
            mediaFileId,
          })
        }
      }

      onConfirm(kit)
    } catch (e) {
      reportError(e)
      showErrorMessageToast('An error occurred')
    }
  }

  return (
    <Button
      size="lg"
      variant="primary"
      onPress={handlePress}
      isDisabled={isLoading || isDisabled}
      fullWidth={fullWidth}
    >
      This Looks Good
    </Button>
  )
}
